import { useMutation } from "@tanstack/react-query";
import { useToast } from "@chakra-ui/react";
import { useApiClient } from "../../../apiClient";
import { translate } from "../../../i18n";
import { CapacityPricingWarningsDto } from "../../../models/court/dto/capacity-pricing-warning.dto";
import { CommitmentCoverageLossWarningsDto } from "../../../models/court/dto/commitment-coverage-loss-warning.dto";
import { CommitmentRepriceWarningsDto } from "../../../models/court/dto/commitment-reprice-warning.dto";

/**
 * What the delete took away, and what it moved. Deleting an availability can
 * reprice as readily as it can un-price: drop a promo window that overlapped
 * the general one and every upcoming booking it was winning falls through to
 * the general row's price — and drop the LAST window over a booked slot and
 * those bookings have no price at all, which `unpricedCommitments` names.
 */
export type DeleteCourtAvailabilityResult = CapacityPricingWarningsDto &
    CommitmentRepriceWarningsDto &
    CommitmentCoverageLossWarningsDto;

export const useDeleteCourtAvailability = (onSuccess: (deletedCourtAvailabilityId: string, result: DeleteCourtAvailabilityResult) => void) => {
    const apiClient = useApiClient();
    const toast = useToast();
    return useMutation({
        mutationKey: ['deleteCourtAvailability'],
        // The body was `{}` and now additively carries what the delete took
        // away (D136) — this window may hold the venue's last pricing row for a
        // size the capacity tab still offers.
        mutationFn: (availabilityId: string) => apiClient.fetchApi<DeleteCourtAvailabilityResult, {}>({ method: 'DELETE', endpoint: `courts/availabilities/${availabilityId}` }),
        onSuccess: (data, courtAvailabilityId) => {
            onSuccess(courtAvailabilityId, data ?? {});
        },
        onError: (error: unknown) => {
            // The API refuses this delete with a precise 409 that names the
            // count, the sport, the CAPACITY and the earliest slot at risk, and
            // states the two remedies ("price the slot for every capacity with
            // a wildcard row, or cancel those games first"). This toast used to
            // print a two-word "Erreur" and drop all of it, so an operator whose
            // delete was blocked by one padel-capacity-2 game in three weeks had
            // nothing to go on and simply clicked again. Same treatment the
            // sibling batch save already gives its 400s in
            // `AddAvailabilitiesModal`.
            toast({
                title: translate('error'),
                description: error instanceof Error ? error.message : undefined,
                status: 'error',
                duration: 8000,
                isClosable: true,
                position: 'top-right',
            });
        }
    });
};
